/**
 * A selection of a list's rows (see `ui/selection.tsx` for its checkboxes and bar). It outlives the page shown and
 * the list's search and filters (rows picked on one page stay picked on the next), and is emptied only when the list
 * itself changes (another space, tab or member) or the page goes. It can also be every row the list's query matches,
 * not just those picked: then a change of the query empties it.
 */
import { useEffect, useMemo, useState } from "preact/hooks";
import { reflow } from "./shift.ts";

export interface Selection {
  /** The ids selected, in the order they were (with `all`: the page's, when every match was selected). */
  readonly ids: readonly string[];
  /** Every row the list's query matches is selected, on whichever page (bulk actions then send the query). */
  readonly all: boolean;
  has: (id: string) => boolean;
  toggle: (id: string, on: boolean) => void;
  /** Selects (or deselects) all of `ids`. */
  setAll: (ids: readonly string[], on: boolean) => void;
  /** Selects every row the query matches, `ids` (the page's) shown among them. */
  selectMatching: (ids: readonly string[]) => void;
  clear: () => void;
}

/** A selected row's background. */
export const SELECTED_ROW = "bg-zinc-100/70";

interface State {
  chosen: ReadonlySet<string>;
  all: boolean;
}

const EMPTY: State = { chosen: new Set(), all: false };

/**
 * A selection of rows, emptied whenever `listKey` (which list it is) changes; selecting every match is undone (and
 * the selection emptied) when `queryKey` (what the list matches: its search and filters) does.
 */
export function useSelection(listKey?: string, queryKey?: string): Selection {
  const [state, setState] = useState<State>(EMPTY);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the key is what resets it.
  useEffect(() => setState(EMPTY), [listKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the key is what resets it.
  useEffect(() => setState((was) => (was.all ? EMPTY : was)), [queryKey]);
  return useMemo(() => {
    const { chosen, all } = state;
    // Chosen in the iOS app, as a view transition (lib/shift.ts): the list's bar giving way to the selection's.
    const choose = (next: State | ((was: State) => State)) => reflow("select", () => setState(next));
    const setAll = (ids: readonly string[], on: boolean) =>
      choose((was) => {
        const next = new Set(was.chosen);
        for (const id of ids) {
          if (on) {
            next.add(id);
          } else {
            next.delete(id);
          }
        }
        // Taking a row out of "every match" leaves the rest of the page's.
        return { chosen: next, all: was.all && on };
      });
    return {
      ids: [...chosen],
      all,
      has: (id) => all || chosen.has(id),
      toggle: (id, on) => setAll([id], on),
      setAll,
      selectMatching: (ids) => choose({ chosen: new Set(ids), all: true }),
      clear: () => choose(EMPTY),
    };
  }, [state]);
}

/** How many a bulk action takes: every match, or those picked. */
export function selectedCount(sel: Selection, total: number | undefined): number {
  return sel.all && total !== undefined ? total : sel.ids.length;
}
